import React, { useState, useEffect, useRef } from 'react'
import { Form, Input, Select, DatePicker, Upload, Modal, InputNumber, message } from 'antd'
import { useMsal } from '@azure/msal-react'
import Button from '../../../components/common/button/button'
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, SaveOutlined, ExclamationCircleOutlined, EyeOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  categoryOptions,
  costCenterOptions,
  paymentMethodOptions,
  currencyOptions,
  getCurrencySymbol,
  handleCurrencyChange as utilHandleCurrencyChange,
  fetchVendors,
  loadDraftData,
  addCostCenterField as utilAddCostCenterField,
  removeCostCenterField as utilRemoveCostCenterField,
  handleVendorChange as utilHandleVendorChange,
  handleSubmit as utilHandleSubmit,
  handleSaveAsDraft as utilHandleSaveAsDraft,
  handleSaveEditedDraft,
  handleSubmitEditedDraft,
  resetFormState,
  getUploadProps,
  uploadAttachmentImmediately,
  deleteAttachmentImmediately,
  currencyValidator // <-- NEW
} from './rfpVendorFormUtils'
import { generateRfpPresignedUrl } from '../../../api/rfpApiService/rfpApiUtils'

const { TextArea } = Input
const { Option } = Select

const RFPForm = ({ visible, onCancel, onSubmit, onSaveAsDraft, editingDraft }) => {
  const [form] = Form.useForm()
  const [attachmentFileList, setAttachmentFileList] = useState([])
  const [loading, setLoading] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [costCenterFields, setCostCenterFields] = useState([{ id: 1 }])
  const [vendorOptions, setVendorOptions] = useState([])
  const [vendorsLoading, setVendorsLoading] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState('PHP')
  const [showCustomCurrency, setShowCustomCurrency] = useState(false)

  const { instance: msalInstance } = useMsal()

  // Serializes immediate attachment upload/delete calls so overlapping
  // selections can't race each other's refreshed-list responses.
  const attachmentQueueRef = useRef(Promise.resolve())
  const enqueueAttachmentOp = (operation) => {
    const result = attachmentQueueRef.current.then(operation, operation)
    attachmentQueueRef.current = result.then(() => {}, () => {})
    return result
  }

  const handleCurrencyChange = (value) => {
    utilHandleCurrencyChange(value, setSelectedCurrency, setShowCustomCurrency, form)
  }

  useEffect(() => {
    if (visible && msalInstance) {
      fetchVendors(msalInstance, setVendorOptions, setVendorsLoading)
    }
  }, [visible, msalInstance])

  useEffect(() => {
    loadDraftData(
      editingDraft,
      visible,
      vendorOptions,
      form,
      setAttachmentFileList,
      setCostCenterFields,
      setSelectedCurrency,
      setShowCustomCurrency
    )
  }, [editingDraft, visible, form, vendorOptions])

  const addCostCenterField = () => {
    utilAddCostCenterField(costCenterFields, setCostCenterFields)
  }

  const removeCostCenterField = (id) => {
    utilRemoveCostCenterField(id, costCenterFields, setCostCenterFields, form)
  }

  const handleVendorChange = (value) => {
    utilHandleVendorChange(value, vendorOptions, form)
  }

  const injectSapReferenceId = (values) => ({
    ...values,
    sapReferenceId: 'NA'
  })

  const handleSubmit = async (values) => {
    const finalValues = injectSapReferenceId(values)

    if (editingDraft && (editingDraft.transactionId || editingDraft._id)) {
      await handleSubmitEditedDraft(
        finalValues,
        costCenterFields,
        vendorOptions,
        attachmentFileList,
        editingDraft,
        msalInstance,
        onSubmit,
        form,
        setAttachmentFileList,
        setCostCenterFields,
        setSelectedCurrency,
        setShowCustomCurrency,
        setLoading
      )
    } else {
      await utilHandleSubmit(
        finalValues,
        costCenterFields,
        vendorOptions,
        attachmentFileList,
        msalInstance,
        onSubmit,
        form,
        setAttachmentFileList,
        setCostCenterFields,
        setSelectedCurrency,
        setShowCustomCurrency,
        setLoading
      )
    }
  }

  const handleSaveAsDraft = async () => {
    const values = form.getFieldsValue()
    const finalValues = injectSapReferenceId(values)
    form.setFieldsValue(finalValues)

    if (editingDraft && (editingDraft.transactionId || editingDraft._id)) {
      await handleSaveEditedDraft(
        form,
        costCenterFields,
        vendorOptions,
        attachmentFileList,
        editingDraft,
        msalInstance,
        onSaveAsDraft,
        setAttachmentFileList,
        setCostCenterFields,
        setSelectedCurrency,
        setShowCustomCurrency,
        setSavingDraft
      )
    } else {
      await utilHandleSaveAsDraft(
        form,
        costCenterFields,
        vendorOptions,
        attachmentFileList,
        editingDraft,
        msalInstance,
        onSaveAsDraft,
        setAttachmentFileList,
        setCostCenterFields,
        setSelectedCurrency,
        setShowCustomCurrency,
        setSavingDraft
      )
    }
  }

  const showSubmitConfirmation = () => {
    Modal.confirm({
      title: 'Confirm Submission',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to submit this RFP? Once submitted, it cannot be edited.',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: () => form.submit(),
      okButtonProps: { style: { backgroundColor: '#ae0270' } }
    })
  }

  const handleCancel = () => {
    resetFormState(form, setAttachmentFileList, setCostCenterFields, setSelectedCurrency, setShowCustomCurrency)
    onCancel()
  }

  const uploadProps = getUploadProps(attachmentFileList, setAttachmentFileList)

  // Existing drafts persist attachment add/remove immediately, like the standalone attachments modal.
  const isEditingExistingDraft = !!(editingDraft && (editingDraft.transactionId || editingDraft._id))

  const handleBeforeUpload = (file) => {
    if (isEditingExistingDraft) {
      const hide = message.loading('Uploading attachment...', 0)
      enqueueAttachmentOp(() => uploadAttachmentImmediately(editingDraft, file, msalInstance))
        .then((updatedList) => {
          setAttachmentFileList(updatedList)
          form.setFieldsValue({ attachments: updatedList })
          message.success('Attachment uploaded successfully.')
        })
        .catch((error) => {
          console.error('Error uploading attachment:', error)
          message.error('Unable to upload this attachment.')
          // Remove the raw file antd's onChange added, since it was never persisted
          setAttachmentFileList((prev) => {
            const reverted = prev.filter((item) => item.uid !== file.uid)
            form.setFieldsValue({ attachments: reverted })
            return reverted
          })
        })
        .finally(hide)
      return false
    }

    return uploadProps.beforeUpload(file)
  }

  const handleRemoveAttachment = (file) => {
    if (isEditingExistingDraft && !file.originFileObj) {
      if (attachmentFileList.length <= 1) {
        message.error('At least one attachment must be present. Add another attachment before deleting this file.')
        return false
      }

      Modal.confirm({
        title: 'Are you sure you want to delete this file?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          try {
            const attachmentId = file._id || file.uid
            const updatedList = await enqueueAttachmentOp(() => deleteAttachmentImmediately(editingDraft, attachmentId, msalInstance))
            setAttachmentFileList(updatedList)
            form.setFieldsValue({ attachments: updatedList })
            message.success('Attachment deleted successfully.')
          } catch (error) {
            console.error('Error deleting attachment:', error)
            message.error('Unable to delete this attachment.')
          }
        }
      })
      return false
    }

    return uploadProps.onRemove(file)
  }

  const getAttachmentUrl = async (file) => {
    if (file.originFileObj) {
      return URL.createObjectURL(file.originFileObj)
    }

    const s3Path = file.s3Location || file.attachmentS3Path || file.url
    if (!s3Path) {
      throw new Error('Attachment location is unavailable')
    }

    return generateRfpPresignedUrl(s3Path, msalInstance)
  }

  const handlePreviewAttachment = async (file) => {
    try {
      const attachmentUrl = await getAttachmentUrl(file)
      window.open(attachmentUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Error previewing attachment:', error)
      message.error('Unable to preview this attachment.')
    }
  }

  const handleDownloadAttachment = async (file) => {
    try {
      const attachmentUrl = await getAttachmentUrl(file)
      const link = document.createElement('a')
      link.href = attachmentUrl
      link.download = file.name || file.filename || file.originalFileName || 'attachment'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading attachment:', error)
      message.error('Unable to download this attachment.')
    }
  }

  const disabledDate = (current) => current && current < dayjs().startOf('day')

  const hasRequiredAttachment = () => {
    // attachmentFileList is kept in sync with the server on every add/remove
    // for existing drafts, so it's the source of truth - editingDraft.attachments
   
    return attachmentFileList.length > 0
  }

  const isSubmitDisabled = () => {
    const values = form.getFieldsValue()
    const errors = form.getFieldsError()
    const hasErrors = errors.some(({ errors: fieldErrors }) => fieldErrors.length > 0)

    const requiredFields = [
      'vendorName', 'invoiceReference', 'dateOfCreation', 'categories',
      'currency', 'requestedAmount', 'filePreparedFor', 'preferredPaymentMethod'
    ]

    const hasAttachment = hasRequiredAttachment()

    let hasAtLeastOneCostCenter = false
    for (const field of costCenterFields) {
      const costCenter = values[`costCenter_${field.id}`]
      const purpose = values[`purpose_${field.id}`]
      if (costCenter && purpose) {
        hasAtLeastOneCostCenter = true
        break
      }
    }

    const allRequiredFilled = requiredFields.every(field => {
      const value = values[field]
      return value !== undefined && value !== null && value !== ''
    })

    return hasErrors || !allRequiredFilled || !hasAtLeastOneCostCenter || !hasAttachment
  }

  const isDraftDisabled = () => {
    const values = form.getFieldsValue()
    const requiredFields = [
      'vendorName', 'invoiceReference', 'dateOfCreation', 'categories',
      'currency', 'requestedAmount', 'filePreparedFor', 'preferredPaymentMethod'
    ]

    const hasAttachment = hasRequiredAttachment()

    let hasAtLeastOneCostCenter = false
    for (const field of costCenterFields) {
      const costCenter = values[`costCenter_${field.id}`]
      const purpose = values[`purpose_${field.id}`]
      if (costCenter && purpose) {
        hasAtLeastOneCostCenter = true
        break
      }
    }

    const allRequiredFilled = requiredFields.every(field => {
      const value = values[field]
      return value !== undefined && value !== null && value !== ''
    })

    return !allRequiredFilled || !hasAtLeastOneCostCenter || !hasAttachment
  }

  return (
    <Modal
      title={<span style={{ fontSize: '20px', fontWeight: '600' }}>
        {editingDraft ? 'Edit Draft RFP' : 'Create New RFP'}
      </span>}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
      className="rfp-form-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={true}
        initialValues={{ dateOfCreation: dayjs(), currency: 'PHP' }}
        preserve={false}
      >
        {/* Vendor Name */}
        <Form.Item
          name="vendorName"
          label="Vendor Name"
          rules={[{ required: true, message: 'Please select vendor name' }]}
        >
          <Select
            placeholder={vendorsLoading ? 'Loading vendors...' : 'Select vendor name'}
            onChange={handleVendorChange}
            showSearch
            loading={vendorsLoading}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={vendorsLoading ? 'Loading...' : 'No active vendors found'}
          >
            {vendorOptions.map(vendor => (
              <Option key={vendor.value} value={vendor.value}>{vendor.label}</Option>
            ))}
          </Select>
        </Form.Item>

        {/* Vendor Info Display */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 2 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#262626', fontSize: '14px' }}>
              Vendor Address
            </label>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.vendorAddress !== curr.vendorAddress}>
              {() => (
                <span style={{
                  color: form.getFieldValue('vendorAddress') ? '#333' : '#666',
                  fontSize: '14px',
                  fontStyle: form.getFieldValue('vendorAddress') ? 'normal' : 'italic',
                  display: 'block',
                  padding: '4px 0',
                  minHeight: '20px'
                }}>
                  {form.getFieldValue('vendorAddress') || 'Vendor address will appear here'}
                </span>
              )}
            </Form.Item>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#262626', fontSize: '14px' }}>
              Vendor ID
            </label>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.vendorId !== curr.vendorId}>
              {() => (
                <span style={{
                  color: form.getFieldValue('vendorId') ? '#333' : '#666',
                  fontSize: '14px',
                  fontStyle: form.getFieldValue('vendorId') ? 'normal' : 'italic',
                  display: 'block',
                  padding: '4px 0',
                  minHeight: '20px'
                }}>
                  {form.getFieldValue('vendorId') || 'ID'}
                </span>
              )}
            </Form.Item>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#262626', fontSize: '14px' }}>
              TIN No.
            </label>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.tinNo !== curr.tinNo}>
              {() => (
                <span style={{
                  color: form.getFieldValue('tinNo') ? '#333' : '#666',
                  fontSize: '14px',
                  fontStyle: form.getFieldValue('tinNo') ? 'normal' : 'italic',
                  display: 'block',
                  padding: '4px 0',
                  minHeight: '20px'
                }}>
                  {form.getFieldValue('tinNo') || 'TIN'}
                </span>
              )}
            </Form.Item>
          </div>
        </div>

        <Form.Item name="vendorAddress" style={{ display: 'none' }}><Input /></Form.Item>
        <Form.Item name="vendorId" style={{ display: 'none' }}><Input /></Form.Item>
        <Form.Item name="tinNo" style={{ display: 'none' }}><Input /></Form.Item>

        {/* Invoice + Date */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="invoiceReference"
            label="Invoice Reference"
            rules={[
              { required: true, message: 'Please enter invoice reference number' },
              { max: 50, message: 'Invoice reference must not exceed 50 characters' },
              { whitespace: true, message: 'Invoice reference cannot be empty spaces' }
            ]}
            style={{ flex: 1 }}
          >
            <Input placeholder="Enter invoice reference number" maxLength={50} />
          </Form.Item>

          <Form.Item
            name="dateOfCreation"
            label="Date of Creation"
            rules={[{ required: true, message: 'Please select date of creation' }]}
            style={{ flex: 1 }}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="Select date" disabledDate={disabledDate} />
          </Form.Item>
        </div>

        {/* Categories + Amount */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <Form.Item
            name="categories"
            label="Categories"
            rules={[{ required: true, message: 'Please select category' }]}
            style={{ flex: 1 }}
          >
            <Select placeholder="Select category" showSearch filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
              {categoryOptions.map(cat => <Option key={cat} value={cat}>{cat}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item label="Amount to Request" required style={{ flex: 1, marginBottom: '24px' }}>
            <Input.Group compact style={{ display: 'flex', gap: 0 }}>
              <Form.Item
                name="currency"
                noStyle
                rules={[
                  { required: true, message: 'Please select or enter currency' },
                  { validator: currencyValidator } // <-- VALIDATION FROM UTILS
                ]}
              >
                {showCustomCurrency ? (
                  <Input
                    placeholder="Enter 3-letter code"
                    maxLength={3}
                    style={{ width: '140px', textTransform: 'uppercase', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    onChange={e => form.setFieldsValue({ currency: e.target.value.toUpperCase() })}
                    suffix={
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setShowCustomCurrency(false)
                          setSelectedCurrency('PHP')
                          form.setFieldsValue({ currency: 'PHP' })
                        }}
                        style={{ padding: 0, height: 'auto', fontSize: '12px', color: '#999' }}
                      >
                        X
                      </Button>
                    }
                  />
                ) : (
                  <Select
                    placeholder="Currency"
                    onChange={handleCurrencyChange}
                    value={selectedCurrency}
                    style={{ width: '140px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  >
                    {currencyOptions.map(c => (
                      <Option key={c.value} value={c.value}>
                        {c.value === 'Others' ? 'Others' : c.value}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>

              <Form.Item
                name="requestedAmount"
                noStyle
                rules={[
                  { required: true, message: 'Please enter requested amount' },
                  { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
                  { type: 'number', max: 999999999999.99, message: 'Amount must not exceed 999,999,999,999.99' }
                ]}
              >
                <InputNumber
                  style={{ width: 'calc(100% - 140px)', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none' }}
                  addonBefore={getCurrencySymbol(selectedCurrency, showCustomCurrency, form)}
                  placeholder="Enter amount"
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={v => v?.replace(/[^\d.]/g, '')}
                  controls={false}
                  min={0.01}
                  max={999999999999.99}
                  precision={2}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </div>

        {/* File Prepared For + Payment Method */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="filePreparedFor"
            label="File Prepared For"
            rules={[
              { required: true, message: 'Please enter who this file is prepared for' },
              { max: 25, message: 'File prepared for must not exceed 25 characters' },
              { whitespace: true, message: 'File prepared for cannot be empty spaces' }
            ]}
            style={{ flex: 1 }}
          >
            <Input placeholder="Enter recipient name" maxLength={25} />
          </Form.Item>

          <Form.Item
            name="preferredPaymentMethod"
            label="Preferred Payment Method"
            rules={[{ required: true, message: 'Please select payment method' }]}
            style={{ flex: 1 }}
          >
         <Select
  placeholder="Select payment method"
  showSearch
  filterOption={(input, option) =>
    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }
>
  {paymentMethodOptions.map(m => <Option key={m} value={m}>{m}</Option>)}
</Select>
          </Form.Item>
        </div>

        {/* Dynamic Cost Centre */}
        {costCenterFields.map((field, idx) => (
          <div key={field.id} style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <Form.Item
                name={`costCenter_${field.id}`}
                label={idx === 0 ? <span>Cost Centre <span style={{ color: 'red' }}></span></span> : ''}
                rules={[{ required: true, message: 'Please select cost centre' }]}
                style={{ flex: 1 }}
              >
            <Select
  placeholder="Select cost centre"
  showSearch
  filterOption={(input, option) =>
    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }
>
  {costCenterOptions.map(c => <Option key={c} value={c}>{c}</Option>)}
</Select>
              </Form.Item>

              <Form.Item
                name={`purpose_${field.id}`}
                label={idx === 0 ? <span>Purpose (Free Text) <span style={{ color: 'red' }}></span></span> : ''}
                rules={[{ required: true, message: 'Please enter purpose' }]}
                style={{ flex: 1 }}
              >
                <TextArea placeholder="Enter purpose details" rows={3} maxLength={500} showCount />
              </Form.Item>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: idx === 0 ? '30px' : '6px' }}>
                {idx === costCenterFields.length - 1 && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addCostCenterField}
                    style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#d63384', borderColor: '#d63384' }}
                    title="Add cost centre"
                  />
                )}
                {idx > 0 && (
                  <Button
                    type="danger"
                    icon={<MinusCircleOutlined />}
                    onClick={() => removeCostCenterField(field.id)}
                    style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Remove cost centre"
                  />
                )}
              </div>
            </div>
          </div>
        ))}


        {/* Upload */}
        <Form.Item
          name="attachments"
          label="Upload Attachments"
          rules={[{ required: true, message: 'Please upload at least one attachment' }]}
          valuePropName="fileList"
          getValueFromEvent={(event) => event?.fileList ?? []}
          initialValue={[]}
        >
          <Upload.Dragger
            {...uploadProps}
            beforeUpload={handleBeforeUpload}
            onRemove={handleRemoveAttachment}
            onPreview={handlePreviewAttachment}
            onDownload={handleDownloadAttachment}
            showUploadList={{
              showPreviewIcon: false,
              showDownloadIcon: false,
              showRemoveIcon: false
            }}
            itemRender={(_, file, __, actions) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </span>
                <button
                  type="button"
                  title="Preview attachment"
                  aria-label={`Preview ${file.name}`}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handlePreviewAttachment(file)
                  }}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: '2px', color: '#595959' }}
                >
                  <EyeOutlined />
                </button>
                <button
                  type="button"
                  title="Download attachment"
                  aria-label={`Download ${file.name}`}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleDownloadAttachment(file)
                  }}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: '2px', color: '#595959' }}
                >
                  <DownloadOutlined />
                </button>
                <button
                  type="button"
                  title="Remove attachment"
                  aria-label={`Remove ${file.name}`}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    actions.remove()
                  }}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: '2px', color: '#8c8c8c' }}
                >
                  <DeleteOutlined />
                </button>
              </div>
            )}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
            style={{ backgroundColor: '#fafafa', border: '2px dashed #d9d9d9', borderRadius: '8px', padding: '20px' }}
          >
            <p className="ant-upload-drag-icon"><UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} /></p>
            <p className="ant-upload-text" style={{ fontSize: '16px', margin: '0 0 8px 0' }}>Click or drag files to upload</p>
            <p className="ant-upload-hint" style={{ fontSize: '14px', color: '#666', margin: 0 }}>
              Support for PDF, DOC, DOCX, JPG, PNG, XLS, XLSX
            </p>
          </Upload.Dragger>
        </Form.Item>

        {/* Buttons */}
        <Form.Item noStyle shouldUpdate>
          {() => (
            <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveAsDraft}
                loading={savingDraft}
                disabled={isDraftDisabled()}
                style={{ backgroundColor: '#f0f0f0', borderColor: '#d9d9d9', color: '#595959' }}
              >
                Save as Draft
              </Button>
              <Button
                type="primary"
                onClick={showSubmitConfirmation}
                loading={loading}
                disabled={isSubmitDisabled()}
              >
                Submit
              </Button>
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default RFPForm